import Cookies from "js-cookie";
import { getGuestEmail } from "@/app/_utils/guestEmail";

export type CartItem = {
  id: string;
  // Null on shipped merch rows (no pickup show).
  show: {
    id: string;
    title: string;
    ticket_price: number;
    img_url: string;
    variant_name?: string;
    inventory_quantity?: number;
    max_quantity_per_order?: number;
    held_quantity?: number;
    next_release_at?: string | null;
  } | null;
  // Set on merch rows; `show` is the pickup show when ships is false.
  merch?: {
    id: string;
    group_id: string;
    title: string;
    img_url?: string | null;
    variant_name?: string | null;
    price: number;
    inventory_quantity?: number | null;
    ships?: boolean;
  } | null;
  quantity: number;
  total_price: number;
};

export type QueuedShow = {
  show_id: string;
  entry_id: string;
  title: string;
  merch_left_out?: boolean;
};

export type EmbeddedCheckoutStart = {
  client_secret: string;
  session_id: string;
  hold_expires_at: string;
  queued?: QueuedShow[];
};

// Every show in the cart was busy: no payment session, only places in line.
export type CheckoutStart = EmbeddedCheckoutStart | { queued: QueuedShow[] };

export type CheckoutHoldStatus = {
  status: "held" | "paid" | "released";
  expires_at: string;
};

export type QueueEntry = {
  id?: string;
  status: "available" | "waiting" | "admitted" | "checking_out" | "done" | "expired" | "left" | "abandoned" | "sold_out";
  show_id?: string;
  requested_quantity?: number;
  admitted_quantity?: number | null;
  initial_ahead?: number;
  ahead?: number;
  hold_expires_at?: string | null;
  message?: string | null;
};

export type Cart = {
  id: string;
  items: CartItem[];
  total_price: number;
  total_items: number;
};

export class CartError extends Error {
  code?: string;
  available?: number;
  held?: number;
  next_release_at?: string | null;

  constructor(body: any, fallback: string) {
    super(body?.error || fallback);
    this.name = "CartError";
    this.code = body?.code;
    this.available = body?.available;
    this.held = body?.held;
    this.next_release_at = body?.next_release_at ?? null;
  }
}

async function cartError(res: Response, fallback: string): Promise<CartError> {
  const body = await res.json().catch(() => null);
  return new CartError(body, fallback);
}

const csrf = () => Cookies.get("csrftoken") ?? "";
const CART_TOKEN_KEY = "cuo_anonymous_cart_token";

function randomCartToken(): string {
  const bytes = new Uint8Array(20);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function getCartToken(): string {
  try {
    const existing = localStorage.getItem(CART_TOKEN_KEY);
    if (existing && /^[a-f0-9]{40}$/.test(existing)) return existing;
    const next = randomCartToken();
    localStorage.setItem(CART_TOKEN_KEY, next);
    return next;
  } catch {
    return "";
  }
}

function cartTokenHeader(): Record<string, string> {
  const token = getCartToken();
  return token ? { "X-CUO-Cart-Token": token } : {};
}

const jsonInit = (method: string, body?: object): RequestInit => ({
  method,
  credentials: "include",
  headers: {
    "Content-Type": "application/json",
    "X-CSRFToken": csrf(),
    ...cartTokenHeader(),
  },
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});

class CartService {
  private baseUrl = `${import.meta.env.VITE_API_BASE_URL}cart/`;

  async getCart(): Promise<Cart> {
    const res = await fetch(this.baseUrl, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...cartTokenHeader(),
      },
    });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return await res.json();
  }

  async addToCart(showId: string, quantity: number = 1, merchId?: string): Promise<CartItem> {
    const body = merchId ? { show_id: showId, quantity, merch_id: merchId } : { show_id: showId, quantity };
    const res = await fetch(this.baseUrl, jsonInit("POST", body));
    if (!res.ok) {
      throw await cartError(res, "Failed to add item to cart");
    }
    return await res.json();
  }

  async addShippedMerch(merchId: string, quantity: number = 1): Promise<CartItem> {
    const res = await fetch(this.baseUrl, jsonInit("POST", { merch_id: merchId, quantity }));
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || "Failed to add item to cart");
    }
    return await res.json();
  }

  async updateItem(itemId: string, quantity: number): Promise<CartItem | null> {
    const res = await fetch(`${this.baseUrl}item/${itemId}/`, jsonInit("PUT", { quantity }));
    if (!res.ok) {
      throw await cartError(res, "Failed to update item");
    }
    if (res.status === 204) return null;
    return await res.json();
  }

  async removeItem(itemId: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}item/${itemId}/`, jsonInit("DELETE"));
    if (!res.ok) throw new Error("Failed to remove item");
  }

  async clearCart(): Promise<void> {
    const res = await fetch(this.baseUrl, jsonInit("DELETE"));
    if (!res.ok) throw new Error("Failed to clear cart");
  }

  async checkout(promoCode?: string, posthogDistinctId?: string): Promise<CheckoutStart> {
    let passwordPerShow: Record<string, { password_group: string; password: string }> = {};
    try {
      passwordPerShow = JSON.parse(sessionStorage.getItem("password_per_show") || "{}");
    } catch {}

    const body: Record<string, unknown> = { ui_mode: "embedded" };
    if (promoCode) body.promo_code = promoCode;
    if (Object.keys(passwordPerShow).length > 0) body.password_per_show = passwordPerShow;
    // Prefill Stripe's email field from the waitlist signup, if we have it.
    // Optional: when absent, checkout proceeds exactly as before with an empty field.
    const guestEmail = getGuestEmail();
    if (guestEmail) body.customer_email = guestEmail;
    if (posthogDistinctId) body.posthog_distinct_id = posthogDistinctId;

    const res = await fetch(`${this.baseUrl}checkout/`, jsonInit("POST", body));
    if (!res.ok) {
      throw await cartError(res, "Failed to initiate checkout");
    }
    // Clear session data after successful checkout redirect initiation
    try { sessionStorage.removeItem("password_per_show"); } catch {}
    try { sessionStorage.removeItem("applied_promo"); } catch {}
    return await res.json();
  }

  async joinQueue(showId: string, quantity: number): Promise<QueueEntry> {
    const res = await fetch(`${this.baseUrl}queue/`, jsonInit("POST", { show_id: showId, quantity }));
    if (!res.ok) throw await cartError(res, "Could not join the queue");
    return await res.json();
  }

  // Null once the entry is gone (purged when the show sells out).
  async queueStatus(entryId: string): Promise<QueueEntry | null> {
    const res = await fetch(`${this.baseUrl}queue/${entryId}/`, jsonInit("GET"));
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Failed to load queue status");
    return await res.json();
  }

  async claimQueueTurn(entryId: string, quantity: number): Promise<QueueEntry> {
    const res = await fetch(`${this.baseUrl}queue/${entryId}/claim/`, jsonInit("POST", { quantity }));
    if (!res.ok) throw await cartError(res, "Could not reserve your tickets");
    return await res.json();
  }

  async leaveQueue(entryId: string): Promise<void> {
    await fetch(`${this.baseUrl}queue/${entryId}/`, jsonInit("DELETE"));
  }

  // Null once the server no longer knows the session.
  async checkoutStatus(sessionId: string): Promise<CheckoutHoldStatus | null> {
    const res = await fetch(`${this.baseUrl}checkout/${encodeURIComponent(sessionId)}/status/`, jsonInit("GET"));
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Failed to load checkout status");
    return await res.json();
  }
}

export default new CartService();
